import { useEffect, useState } from "react";
import { useRoute, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useLibrary } from "@/contexts/LibraryContext";
import { useMastermind } from "@/contexts/MastermindContext";
import { documentSharingService } from "@/services/documentSharingService";
import { knowledgeBaseService, type KnowledgeBaseItem } from "@/services/knowledgeBaseService";
import { spaceSharingService } from "@/services/spaceSharingService";
import { supabase } from "@/lib/supabase";
import { effectiveItemActions } from "@/services/sharedSpacePermissionService";
import { getSpaceIdsFromKbItem } from "@/lib/kbDocumentLinks";
import { DocumentViewerModal } from "@/components/DocumentViewerModal";
import { PublicDocumentShareView } from "@/components/PublicDocumentShareView";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, Loader2 } from "lucide-react";

async function resolveCanEditForDocument(
  doc: KnowledgeBaseItem,
  userId: string
): Promise<boolean> {
  if (doc.userId === userId) return true;
  const spaceIds = getSpaceIdsFromKbItem(doc);
  for (const spaceId of spaceIds) {
    const role = await spaceSharingService.getCurrentUserSpaceRole(spaceId);
    if (!role) continue;
    const { data: spaceRow } = await supabase
      .from("mastermind")
      .select("user_id")
      .eq("id", spaceId)
      .maybeSingle();
    const overrides = await spaceSharingService.getMyItemOverridesForSpace(spaceId);
    const actions = effectiveItemActions(
      role,
      "document",
      doc.id,
      doc.userId,
      userId,
      spaceRow?.user_id != null ? String(spaceRow.user_id) : null,
      overrides
    );
    if (actions.canEdit) return true;
  }
  return false;
}

export default function DocumentShareOpenPage() {
  const [, params] = useRoute("/documents/share/:token");
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { setSelectedDocumentId, setSelectedReferenceId, setLibrarySidebarOpen } = useLibrary();
  const { setIsCollapsed } = useMastermind();
  const token = params?.token || "";

  const [modalDoc, setModalDoc] = useState<KnowledgeBaseItem | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [routed, setRouted] = useState(false);

  const { data: validation, isPending, isFetching, isFetched } = useQuery({
    queryKey: ["/api/documents/share/validate", token, user?.id],
    queryFn: () => documentSharingService.validatePublicLink(token),
    enabled: !!token,
    retry: false,
  });

  const isResolvingValidation = !token || isPending || isFetching || !isFetched;

  useEffect(() => {
    if (routed || isResolvingValidation || !validation?.isValid || !validation.document || !user) {
      return;
    }

    let cancelled = false;

    (async () => {
      const publicDoc = validation.document!;
      let doc: KnowledgeBaseItem | null = null;
      try {
        doc = await knowledgeBaseService.getKnowledgeBaseItem(publicDoc.id);
      } catch {
        doc = null;
      }
      if (!doc) {
        doc = documentSharingService.validationDocumentToKnowledgeBaseItem(publicDoc);
      }

      const canEdit = await resolveCanEditForDocument(doc, user.id);
      if (cancelled) return;

      if (canEdit) {
        setSelectedReferenceId(null);
        setSelectedDocumentId(publicDoc.id);
        setLibrarySidebarOpen(true);
        setIsCollapsed(false);
        setRouted(true);
        setLocation("/library");
        return;
      }

      setModalDoc(doc);
      setModalOpen(true);
      setRouted(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    validation,
    isResolvingValidation,
    user,
    routed,
    token,
    setSelectedDocumentId,
    setSelectedReferenceId,
    setLibrarySidebarOpen,
    setIsCollapsed,
    setLocation,
  ]);

  const handleModalClose = (open: boolean) => {
    setModalOpen(open);
    if (!open) {
      setLocation("/library");
    }
  };

  if (isResolvingValidation) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!validation?.isValid || !validation.document) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Link unavailable
            </CardTitle>
            <CardDescription>
              {validation?.message || "This share link is invalid or has expired."}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (modalOpen && modalDoc) {
    return (
      <>
        <div className="flex h-full min-h-[50vh] flex-col opacity-0 pointer-events-none" aria-hidden>
          <PublicDocumentShareView
            document={{
              id: modalDoc.id,
              title: modalDoc.title,
              content: modalDoc.content ?? null,
              itemType: "note",
            }}
          />
        </div>
        <DocumentViewerModal
          open={modalOpen}
          onOpenChange={handleModalClose}
          document={modalDoc}
        />
      </>
    );
  }

  if (routed) {
    return null;
  }

  return (
    <div className="flex h-full items-center justify-center p-8">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}
